import { ReactNode, useEffect, useState } from "react";

import { getClientIgnitionConfigTemplates } from "../../lib/automatic";
import type { ConfirmAction } from "../../lib/axleAi/types";
import JsonCode from "./JsonCode";

const codeBlockClassName =
  "bg-white text-gray-900 border border-gray-200 text-xs rounded-md p-4 overflow-x-auto";

type CardShellProps = {
  title: string;
  subtitle: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled: boolean;
  children: ReactNode;
};

const CardShell = ({
  title,
  subtitle,
  confirmLabel,
  onConfirm,
  onCancel,
  disabled,
  children,
}: CardShellProps) => (
  <div className="border border-gray-300 rounded-lg p-4 flex flex-col gap-y-3 bg-gray-50">
    <div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
    </div>
    {children}
    <div className="flex gap-x-2">
      <button
        onClick={onConfirm}
        disabled={disabled}
        className="px-4 py-2 bg-black text-white text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
      >
        {confirmLabel}
      </button>
      <button
        onClick={onCancel}
        disabled={disabled}
        className="px-4 py-2 border border-gray-300 bg-white text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        Cancel
      </button>
    </div>
  </div>
);

type IgnitionTemplate = { id: string; displayName?: string };

/** What the user chose in the card before confirming. */
export type ConfirmOptions = {
  /** The ignition template chosen, if not the default config. */
  templateId?: string;
  /** The support email as the user edited it. */
  email?: { subject: string; body: string };
};

type AxleAiActionCardProps = {
  action: ConfirmAction;
  clientId: string;
  clientName: string;
  onConfirm: (options?: ConfirmOptions) => void;
  onCancel: () => void;
  disabled: boolean;
};

/**
 * Shows an action Axle AI proposed so the user can confirm or cancel it.
 * Nothing is written until onConfirm runs.
 */
const AxleAiActionCard = ({
  action,
  clientId,
  clientName,
  onConfirm,
  onCancel,
  disabled,
}: AxleAiActionCardProps) => {
  const [templates, setTemplates] = useState<IgnitionTemplate[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState(
    action.type === "email_support" ? action.subject : ""
  );
  const [body, setBody] = useState(
    action.type === "email_support" ? action.body : ""
  );

  // Same template list New Ignition Session offers; "" is the default config.
  useEffect(() => {
    if (action.type !== "create_ignition_session") return;
    getClientIgnitionConfigTemplates(clientId).then(([data]) =>
      setTemplates(data?.templates ?? [])
    );
  }, [action.type, clientId]);

  if (action.type === "update_client_config") {
    return (
      <CardShell
        title="Confirm config update"
        subtitle={`${action.clientName} (${action.clientId}) — nothing changes until you confirm.`}
        confirmLabel="Confirm update"
        onConfirm={() => onConfirm()}
        onCancel={onCancel}
        disabled={disabled}
      >
        <pre className={codeBlockClassName}>
          <JsonCode
            text={JSON.stringify(
              { displayName: action.displayName, config: action.config },
              null,
              2
            )}
          />
        </pre>
      </CardShell>
    );
  }

  if (action.type === "email_support") {
    return (
      <CardShell
        title={
          action.topic === "client_config_read"
            ? "Email Axle support: reading a client config"
            : "Email Axle support: updating a client config"
        }
        subtitle="To support@axle.insure — replies come to you. Edit it before sending if you like."
        confirmLabel="Send email"
        onConfirm={() =>
          onConfirm({ email: { subject: subject.trim(), body: body.trim() } })
        }
        onCancel={onCancel}
        disabled={disabled || !subject.trim() || !body.trim()}
      >
        <label className="flex flex-col gap-y-1 text-sm">
          <span className="font-medium text-gray-700">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={disabled}
            className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-black"
          />
        </label>
        <label className="flex flex-col gap-y-1 text-sm">
          <span className="font-medium text-gray-700">Message</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={disabled}
            rows={8}
            className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-black"
          />
        </label>
      </CardShell>
    );
  }

  const { firstName, lastName, email, phone } = action.user;

  return (
    <CardShell
      title="Confirm new ignition session"
      subtitle={`${clientName} — the user and session are only created once you confirm.`}
      confirmLabel="Create session"
      onConfirm={() => onConfirm({ templateId: templateId || undefined })}
      onCancel={onCancel}
      disabled={disabled}
    >
      <table className="text-sm bg-white border border-gray-200 rounded-md">
        <tbody>
          {[
            ["Name", `${firstName} ${lastName}`],
            ["Email", email],
            ["Phone", phone],
          ].map(([label, value]) => (
            <tr key={label} className="border-b border-gray-100 last:border-0">
              <td className="px-3 py-2 text-gray-500 w-24">{label}</td>
              <td className="px-3 py-2">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <label className="flex flex-col gap-y-1 text-sm">
        <span className="font-medium text-gray-700">Ignition template</span>
        <select
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          disabled={disabled}
          className="w-fit border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-black"
        >
          <option value="">Default</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.displayName || "Untitled template"}
            </option>
          ))}
        </select>
      </label>
    </CardShell>
  );
};

export default AxleAiActionCard;
